import type { LngLatBoundsLike, ViewStateChangeEvent } from 'react-map-gl/mapbox';

import { getMaps as apiGetMaps } from '~/api/projects2';
import type { Project } from '~/common/types';

interface ProjectMapBoundProps {
  ne: {
    lat: number;
    lng: number;
  };
  sw: {
    lat: number;
    lng: number;
  };
}

interface SaveBoundsToLocalStorageProps {
  e: ViewStateChangeEvent;
  currentMapId?: string;
}

const localStorageName = 'projectMapBounds';

export const getMapBounds = (currentMapId?: string) => {
  if (!currentMapId) return;
  const storedBounds = localStorage.getItem(localStorageName);
  if (storedBounds === null) return;
  const mapBounds = new Map(JSON.parse(storedBounds)) as Map<string, ProjectMapBoundProps>;
  const currentBound = mapBounds.get(currentMapId);
  if (!currentBound) return;
  const { sw, ne } = currentBound;
  return [
    [sw.lng, sw.lat],
    [ne.lng, ne.lat],
  ] as LngLatBoundsLike;
};

export const saveBoundsToLocalStorage = ({ e, currentMapId }: SaveBoundsToLocalStorageProps) => {
  if (!currentMapId) return;
  const bounds = e.target.getBounds();
  if (!bounds) return;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const storedBounds = localStorage.getItem(localStorageName);

  if (storedBounds === null) {
    // No bounds exist, create one
    const projectMapBoundsMap = new Map();
    projectMapBoundsMap.set(currentMapId, { ne, sw });

    localStorage.setItem(localStorageName, JSON.stringify([...projectMapBoundsMap]));
  } else {
    const mapBounds = new Map(JSON.parse(storedBounds)) as Map<string, ProjectMapBoundProps>;
    const currentBound = mapBounds?.get(currentMapId);
    if (currentBound) {
      // Current map bound exists, update it
      mapBounds.set(currentMapId, { ne, sw });
      localStorage.setItem(localStorageName, JSON.stringify([...mapBounds]));
    } else {
      // Bounds exist but current map bound does not exist, create one
      mapBounds.set(currentMapId, { ne, sw });
      localStorage.setItem(localStorageName, JSON.stringify([...mapBounds]));
    }
  }
};

export const deleteMapBoundsInLocalStorage = async (currentMapId?: string) => {
  if (!currentMapId) return;
  const storedBounds = localStorage.getItem(localStorageName);
  if (storedBounds === null) return;
  const mapBounds = new Map(JSON.parse(storedBounds)) as Map<string, ProjectMapBoundProps>;
  // Delete the current map bounds if it exists
  if (!mapBounds.has(currentMapId)) return;
  mapBounds.delete(currentMapId);
  localStorage.setItem(localStorageName, JSON.stringify([...mapBounds]));
};

export const deleteProjectMapBoundsInLocalStorage = async (projectId?: string) => {
  if (!projectId) return;
  const maps = (await apiGetMaps(projectId)) as Project['maps'];
  for (const key in maps) {
    const m = maps[key];
    await deleteMapBoundsInLocalStorage(m.id);
  }
};
